import type { CourseModel, CoursesState, KeyValuePair, ParticipantExtendedModel } from './types';

export const getExistingCourse = (
  courses: CourseModel[] | undefined,
  courseId: string
): CourseModel | undefined | null => {
  if (!courses || !courseId) {
    return null;
  }

  const normalizedCourseId = String(courseId);
  return courses.find(course => String(course.id) === normalizedCourseId);
};

export const getCourseById = (
  courses: CourseModel[] | undefined,
  courseId: string
): CourseModel | null => {
  if (!courses || !courseId) {
    return null;
  }

  const normalizedCourseId = String(courseId);
  return courses.find(course => String(course.id) === normalizedCourseId) ?? null;
};

export const getCourseParticipants = (
  courses: CourseModel[] | undefined,
  courseId: string
): ParticipantExtendedModel[] => {
  const course = getCourseById(courses, courseId);
  return course?.participants ?? [];
};

export const getCourseParticipant = (
  courses: CourseModel[] | undefined,
  courseId: string,
  participantId: string
): ParticipantExtendedModel | null => {
  const participants = getCourseParticipants(courses, courseId);
  return participants.find(participant => participant.id === participantId) ?? null;
};

export const updateCourseParticipant = (
  courses: CourseModel[],
  courseId: string,
  participantId: string,
  updates: Partial<ParticipantExtendedModel>
): CourseModel[] => {
  return courses.map(course => {
    if (course.id !== courseId) {
      return course;
    }

    return {
      ...course,
      participants: (course.participants ?? []).map(participant => {
        if (participant.id !== participantId) {
          return participant;
        }

        return {
          ...participant,
          ...updates
        };
      })
    };
  });
};

export const addCourseParticipant = (
  courses: CourseModel[],
  courseId: string,
  participant: ParticipantExtendedModel
): CourseModel[] => {
  return courses.map(course => {
    if (course.id !== courseId) {
      return course;
    }

    return {
      ...course,
      participants: [...(course.participants ?? []), participant]
    };
  });
};

export const removeCourseParticipant = (
  courses: CourseModel[],
  courseId: string,
  participantId: string
): CourseModel[] => {
  return courses.map(course => {
    if (course.id !== courseId) {
      return course;
    }

    return {
      ...course,
      participants: (course.participants ?? []).filter(
        participant => participant.id !== participantId
      )
    };
  });
};

export const changeCourseValues = (
  state: CoursesState,
  courseId: string | undefined | null,
  keyValues: KeyValuePair[]
): void => {
  const existingCourse = getExistingCourse(state.courses, courseId as string);

  if (existingCourse) {
    for (const keyValue of keyValues) {
      (existingCourse as any)[keyValue.key] = keyValue.value;
    }
  }
};

export const changeParticipantValues = (
  state: CoursesState,
  courseId: string | undefined | null,
  participantId: string | undefined | null,
  keyValues: KeyValuePair[]
): void => {
  const existingParticipant = getCourseParticipant(
    state.courses,
    courseId as string,
    participantId as string
  );

  if (existingParticipant) {
    for (const keyValue of keyValues) {
      (existingParticipant as any)[keyValue.key] = keyValue.value;
    }
  }
};

export const setHasFreshStart = (state: CoursesState, courseId: string, value: boolean): void => {
  const existingCourse = getExistingCourse(state.courses, courseId as string);

  if (existingCourse) {
    existingCourse.hasFreshStart = value;
  }
};
